/**
 * Dashboard block "AI-samenvatting": a short briefing for today, made only when
 * the user clicks (one request). The last one stays until the next click.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles } from 'lucide-react';
import { AiError, runSummary, summaryMessage, type AiErrorCode } from '@nexus/ai';
import { useStore, useStoreQuery } from '../db/context';
import { useUi } from '../state/ui';
import { AnswerText } from './AnswerText';
import { transport, useAiChat } from './chat';
import { useAiReady } from './providers';

/** On this device only: the briefing is derived from notes that sync anyway. */
const SETTING = 'ai_summary';

interface Saved {
  text: string;
  at: number;
}

export function SummaryBlock() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const { open } = useUi();
  const ready = useAiReady('summary').status === 'ready';
  const stored = useStoreQuery(
    async (s) => {
      const raw = await s.getSetting(SETTING);
      try {
        return raw ? (JSON.parse(raw) as Saved) : null;
      } catch {
        return null;
      }
    },
    [],
    ['settings'],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<AiErrorCode | null>(null);
  // Local settings send no change signal: show the new one right away.
  const [fresh, setFresh] = useState<Saved | null>(null);
  const saved = fresh ?? stored;

  const make = async () => {
    setBusy('');
    setError(null);
    try {
      const text = await runSummary({
        transport,
        model: 'sonnet',
        message: await summaryMessage(store),
        onText: (soFar) => setBusy(soFar),
        onUsage: (usage) => useAiChat.setState({ usage }),
      });
      const next: Saved = { text, at: Date.now() };
      await store.setSetting(SETTING, JSON.stringify(next));
      setFresh(next);
    } catch (e) {
      if (!(e instanceof AiError)) console.error('ai summary', e);
      setError(e instanceof AiError ? e.code : 'api');
    } finally {
      setBusy(null);
    }
  };
  const openTitle = async (title: string) => {
    const page = await store.findPageByName(title);
    if (page) open({ kind: 'page', pageId: page.id });
  };
  const when = saved
    ? new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(saved.at)
    : null;

  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="ai-summary">
      {busy !== null ? (
        busy ? (
          <AnswerText text={busy} onLink={(x) => void openTitle(x)} />
        ) : (
          <p className="flex items-center gap-2 text-xs text-fg-muted">
            <Loader2 className="size-3.5 animate-spin" />
            {t('ai.summary.making')}
          </p>
        )
      ) : saved ? (
        <AnswerText text={saved.text} onLink={(x) => void openTitle(x)} />
      ) : (
        <p className="text-fg-muted">{t('ai.summary.intro')}</p>
      )}
      {error && (
        <p role="alert" className="rounded-md bg-red-500/10 px-2 py-1.5 text-danger">
          {t(`ai.errors.${error}`)}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy !== null || !ready}
          onClick={() => void make()}
          className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-bg-hover disabled:opacity-50"
        >
          <Sparkles className="size-3.5 text-accent-ink" />
          {saved ? t('ai.summary.again') : t('ai.summary.make')}
        </button>
        {!ready ? (
          <span className="text-xs text-fg-muted">{t('ai.summary.signIn')}</span>
        ) : (
          when && <span className="text-xs text-fg-muted">{t('ai.summary.madeAt', { when })}</span>
        )}
      </div>
    </div>
  );
}
